import React, { Fragment, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { Button, Heading, Grid, GridCell, Flex, FlexCell } from 'components'
import {
  ColumnExtended,
  FilterRequestList,
  FilterTypes,
  generateUrlFilterParams,
  padding,
  TopSection,
} from '@evrekadev/evreka-ui-components'
import { Filter } from './Filter'
import { getRequestFilters, parseFilters } from 'components/MapV2/utils'
import { useTranslation } from 'utils/useTranslation'
import { useInternalMapParams } from 'utils/useInternalMapParams'
import debounce from 'lodash-es/debounce'
import { useMapContext } from 'components/MapV2/MapProvider'
import { useSelector } from 'react-redux'
import { selectCurrentTimezone } from 'services/slices/auth'
import i18n from 'utils/i18n'
import { MapFiltersStyled } from './MapFilters.style'
import { useMap } from 'react-leaflet'

const StyledFilterSection = styled.div`
  ${padding([10])}
  height: calc(100% - 2rem);
  overflow: auto;
`

export type FilterSectionProps = {
  columns?: ColumnExtended<any>[]
  onFetch: ((filter: FilterRequestList) => void) | undefined
}

export const FilterSection: React.FC<React.PropsWithChildren<FilterSectionProps>> = ({
  onFetch,
  columns,
}) => {
  const { t } = useTranslation('components.map')
  const timezone = useSelector(selectCurrentTimezone)
  const { caption, disableInternalParams, isRtl } = useMapContext()
  const mapFilterRef = React.useRef<HTMLDivElement>(null)
  const map = useMap()

  useEffect(() => {
    if (mapFilterRef.current) {
      if (mapFilterRef.current) {
        mapFilterRef.current.addEventListener('mouseenter', () => {
          map.dragging.disable()
          map.doubleClickZoom.disable()
          map.scrollWheelZoom.disable()
        })
        mapFilterRef.current.addEventListener('mouseleave', () => {
          map.dragging.enable()
          map.doubleClickZoom.enable()
          map.scrollWheelZoom.enable()
        })
      }
    }
  }, [mapFilterRef])

  const {
    filterByValues: filters,
    setFilterBy: setFilters,
    searchParams: _,
    setSearchParams,
  } = useInternalMapParams(caption || null, '', disableInternalParams)

  const onFilterChange = debounce(
    useCallback(
      (id: string, value: string | string[], type: string) => {
        setFilters((prev) => parseFilters(prev, i18n, id, value, type))
      },
      [setFilters, i18n],
    ),
    100,
  )

  if (!columns) return null

  const filterArray = columns.map((column) => {
    const columnId = column.id
    const isFilterTypeDefined = column.filterType

    if (column.disableFilters) {
      return null
    }

    return (
      isFilterTypeDefined &&
      columnId && (
        <Fragment key={columnId}>
          <TopSection label={column.Header as string} />
          <Filter
            key={columnId}
            column={column}
            filterValue={filters[columnId]}
            onChange={(id: string, value: string | string[]) =>
              onFilterChange(id, value, isFilterTypeDefined || FilterTypes.TEXT)
            }
            type={isFilterTypeDefined || FilterTypes.TEXT}
            cellType={column?.cellType}
            timezone={timezone}
          />
        </Fragment>
      )
    )
  })

  return (
    <MapFiltersStyled isRtl={isRtl} ref={mapFilterRef}>
      <StyledFilterSection>
        <Grid gap={1}>
          <Heading variant="h2">{t('filters')}</Heading>
          {filterArray.map((el) => el && <GridCell key={el.key}>{el}</GridCell>)}
        </Grid>
      </StyledFilterSection>

      <Flex gap={1} center="zero">
        <FlexCell>
          <Button
            variant="outline"
            color="secondary"
            data-testid="map_clear_filters"
            onClick={() => {
              setFilters({})

              setSearchParams({
                filterBy: generateUrlFilterParams({}),
              })

              if (onFetch) {
                onFetch([])
              }
            }}
          >
            {t('clear_filters')}
          </Button>
        </FlexCell>

        <FlexCell>
          <Button
            onClick={() => {
              const requestFilters: FilterRequestList = getRequestFilters(columns, filters)

              setSearchParams({
                filterBy: generateUrlFilterParams(filters),
              })

              if (onFetch) {
                onFetch(requestFilters)
              }
            }}
            data-testid="map_apply_filters"
          >
            {t('apply_filters')}
          </Button>
        </FlexCell>
      </Flex>
    </MapFiltersStyled>
  )
}
